import React, { useState, useMemo, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col
} from 'reactstrap';
import classnames from 'classnames';
import TableContainerReactTable from 'Components/Common/TableContainerReactTable';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';
import { useLazySearchLocationQuery, Location } from 'features/common/location';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';
import { toast } from 'react-toastify';


interface AssociateLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (location: Location | Location[]) => void; // Updated to handle both single and multiple selections
  title?: string; // Add optional title prop
  multiSelect?: boolean; // Add optional multi-select mode
}


const searchFieldOptions: Array<{ label: string; value: keyof Location }> = [
  { label: 'Store Name', value: 'name' }
];

const AssociateLocationModal = ({ isOpen, onClose, onSelect, title = "Associate Location", multiSelect = false }: AssociateLocationModalProps) => {
  const [activeTab] = useState('1');
  const [searchField, setSearchField] = useState<keyof Location>('name');
  const [searchValue, setSearchValue] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]); // For multi-select mode

  const [searchLocation, { data: locationData, isLoading: isLocationLoading }] = useLazySearchLocationQuery();

  const handleRowClick = useCallback((location: Location) => {
    if (!multiSelect) {
      setSelectedLocation(location);
      // Immediately select the location when row is clicked (single select mode)
      if (onSelect) {
        onSelect(location);
      }
    }
  }, [onSelect, multiSelect]);

  const handleRowDoubleClick = useCallback((location: Location) => {
    if (!multiSelect && onSelect) {
      onSelect(location);
      onClose();
    }
  }, [onSelect, onClose, multiSelect]);

  // Multi-select handlers
  const handleCheckboxChange = useCallback((location: Location, isChecked: boolean) => {
    if (isChecked) {
      setSelectedLocations(prev => [...prev, location]);
    } else {
      setSelectedLocations(prev => prev.filter(l => l.rowId !== location.rowId));
    }
  }, []);

  const handleSelectAll = useCallback((isChecked: boolean) => {
    if (isChecked) {
      setSelectedLocations(Array.isArray(locationData?.data) ? locationData.data : []);
    } else {
      setSelectedLocations([]);
    }
  }, [locationData?.data]);

  const isLocationSelected = useCallback((location: Location) => {
    return selectedLocations.some(l => l.rowId === location.rowId);
  }, [selectedLocations]);

  const isAllSelected = useCallback(() => {
    const dataArray = Array.isArray(locationData?.data) ? locationData.data : [];
    return dataArray.length > 0 && selectedLocations.length === dataArray.length;
  }, [locationData?.data, selectedLocations]);

  const columns = useMemo(() => {
    const baseColumns = [
      ...(multiSelect ? [{
        header: () => (
          <div className="d-flex justify-content-center align-items-center">
            <input
              type="checkbox"
              checked={isAllSelected()}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="form-check-input"
            />
          </div>
        ),
        accessorKey: 'pick',
        enableColumnFilter: false,
        size: 50,
        cell: (cell: any) => (
          <div className="d-flex justify-content-center align-items-center">
            <input
              type="checkbox"
              checked={isLocationSelected(cell.row.original)}
              onChange={(e) => handleCheckboxChange(cell.row.original, e.target.checked)}
              className="form-check-input"
            />
          </div>
        )
      }] : []),
      {
        header: 'Store Name',
        accessorKey: 'name',
        enableColumnFilter: false,
        cell: ({ row }: { row: { original: Location } }) => (
          <div
            onClick={() => handleRowClick(row.original)}
            onDoubleClick={() => handleRowDoubleClick(row.original)}
            style={{ cursor: multiSelect ? 'default' : 'pointer' }}
            className={!multiSelect && selectedLocation?.rowId === row.original.rowId ? 'table-active' : ''}
          >
            <span className={multiSelect ? '' : 'text-link'} style={{ textDecoration: multiSelect ? 'none' : 'underline' }}>
              {row.original.name}
            </span>
          </div>
        )
      },
      { 
        header: 'Name', 
        accessorKey: 'shortName', 
        enableColumnFilter: false
      },
      { 
        header: 'Addr 1', 
        accessorKey: 'addressLine1', 
        enableColumnFilter: false
      },
      { 
        header: 'Addr 2', 
        accessorKey: 'addressLine2', 
        enableColumnFilter: false
      },
    ];
    return baseColumns;
  }, [selectedLocation, multiSelect, handleRowClick, handleRowDoubleClick, handleCheckboxChange, handleSelectAll, isLocationSelected, isAllSelected]);

  const handleSearch = async () => {
    try {
      // Use utility function to format search parameters with '%' default
      const searchParam = searchValue.trim() || '%';
      await searchLocation({ locationName: searchParam }).unwrap();
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setSelectedLocation(null);
    setSelectedLocations([]);
  };

  const handleSelect = () => {
    if (multiSelect && selectedLocations.length > 0 && onSelect) {
      onSelect(selectedLocations);
      setSelectedLocations([]);
      onClose();
    }
  };

  const handleClears = () => {
    setSelectedLocation(null);
    setSelectedLocations([]);
  };

  const handleCancel = () => {
    setSelectedLocation(null);
    setSelectedLocations([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink className={classnames({ active: activeTab === '1' })} tabIndex={0} aria-label="Search Tab">
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Row className="mb-2 justify-content-center align-items-center gap-2">
              <Col md={4}>
                <SelectInput
                  formData={{ searchField }}
                  setFormData={obj => setSearchField(obj.searchField)}
                  label="Search"
                  id="searchField"
                  options={searchFieldOptions.map(opt => opt.label)}
                />
              </Col>
              <Col md={4}>
                <FormInput
                  id="searchValue"
                  label="For value"
                  value={searchValue}
                  onChange={setSearchValue}
                  className="form-control-sm"
                />
              </Col>
              <Col md={2} className="d-flex gap-2">
                <button className="btn btn-soft-primary btn-sm" style={{minWidth: 80 }} onClick={handleSearch}>Search</button>
                <button className="btn btn-soft-warning btn-sm" style={{minWidth: 80 }} onClick={handleClear}>Clear</button>

              </Col>
              <Col md={1}>
              </Col>
            </Row>
            {isLocationLoading ? (
              <LoadingIndicator
                isLoading={isLocationLoading}
                isProcessing={false}
                error={null}
                title="Loading locations from server..."
                height="200px"
              />
            ) : (
              <TableContainerReactTable
                columns={columns}
                data={Array.isArray(locationData?.data) ? locationData.data : []}
                isGlobalFilter={false}
                customPageSize={10}
                tableClass="table-centered align-middle table-nowrap mb-0"
                theadClass="table-light"
                thClass="px-3 py-2"
                verticalLines={true}
              />
            )}
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div className="text-muted">
          {multiSelect && selectedLocations.length > 0 && (
            <span>{selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''} selected</span>
          )}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-soft-warning btn-sm" style={{minWidth: 80 }} onClick={handleClears}>
            Clear{multiSelect ? 's' : ''}
          </button>
          {multiSelect && (
            <button 
              className="btn btn-soft-success btn-sm" 
              style={{minWidth: 80 }} 
              onClick={handleSelect}
              disabled={selectedLocations.length === 0}
            >
              Select
            </button>
          )}
          <button className="btn btn-soft-dark btn-sm" style={{minWidth: 80 }} onClick={handleCancel}>Cancel</button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AssociateLocationModal; 